import type { AngleDOuverture, FondDeProjet, FormeDeBulle, FormeDeProjet, ObjetDeBulle, TraitDeProjet } from '@nextilt/commun';
import { FORME_GALET, FORME_RECTANGLE } from './parametres';

/**
 * Les mots du panneau « Habit » d'un projet (chantier habit-du-projet, ticket 02, décisions d'Igor
 * du 2026-10-20) : l'onglet Habit du panneau « Mode » (`mots/modes.ts`), et ses choix. « Thème »
 * est gardé pour les thèmes tout prêts.
 */
export const HABIT_DU_PROJET = 'L’habit du projet';
export const EXPLICATION_DE_L_HABIT = 'La carte, la hiérarchie et les fiches de ce projet.';
export const COULEUR_DU_PROJET = 'Couleur';
export const FOND_DE_LA_CARTE = 'Fond de la carte';
export const FORME_DES_BULLES_DU_PROJET = 'Forme des bulles';
export const TRAIT_DES_BULLES = 'Trait';
/** L'angle sur lequel le projet s'ouvre (chantier angle-a-l-ouverture), dans son Habit et dans Paramètres. */
export const S_OUVRE_EN = 'S’ouvre en';
export const ANGLE_A_L_OUVERTURE = 'Angle à l’ouverture';
/** L'habit n'a pas pu s'enregistrer : il revient à ce qu'il était. */
export const HABIT_PAS_ECRIT = 'L’habit n’a pas pu s’enregistrer, réessaie';

/** Les trois fonds de la carte d'un projet, dans l'ordre du panneau. */
export const MOTS_DES_FONDS_DE_PROJET: Record<FondDeProjet, string> = {
  uni: 'Uni',
  lignes: 'Lignes',
  carreaux: 'Carreaux',
  bristol: 'Bristol',
  postit: 'Post-it',
};

/** Les huit formes d'une bulle (Galet et Rectangle, les mots de Paramètres). */
export const MOTS_DES_FORMES_DE_PROJET: Record<FormeDeProjet, string> = {
  galet: FORME_GALET,
  rond: 'Rond',
  etiquette: 'Étiquette',
  ticket: 'Ticket',
  bristol: 'Fiche bristol',
  postit: 'Post-it',
  scotch: 'Bout de scotch',
  rectangle: FORME_RECTANGLE,
};

/** Les cinq traits d'une bulle. */
export const MOTS_DES_TRAITS_DE_PROJET: Record<TraitDeProjet, string> = {
  fin: 'Fin',
  gros: 'Gros',
  feutre: 'Feutre',
  craie: 'Craie',
  crayon: 'Crayon',
};

/**
 * Les angles sur lesquels un projet peut s'ouvrir, les noms des onglets de la tête du projet
 * (`projets/angles.ts` les lit ici).
 */
export const MOTS_DES_ANGLES_D_OUVERTURE: Record<AngleDOuverture, string> = {
  carte: 'Carte',
  liste: 'Liste',
  hierarchie: 'Hiérarchie',
};

/*
 * L'habit d'une bulle (chantier habit-des-bulles, modification 04) : l'espace « Habit » de sa
 * fiche, et son menu « ⋯ » sur la carte (05). Trois rangées, chacune avec « Comme le projet » en
 * tête : la bulle revient à l'habit du projet (pour la couleur, à celle de sa mère, sinon à
 * l'automatique).
 */
export const HABIT_DE_LA_BULLE = 'Habit';
/** Le menu « ⋯ » sur une bulle de la carte (chantier habit-des-bulles, modification 05). */
export const HABILLER_LA_BULLE = 'Habiller cette bulle';
/* « Comme le projet » est celui du menu « Type ▾ » (`mots/fiche.ts`), le même mot. */
export const FORME_DE_LA_BULLE = 'Forme';
export const COULEUR_DE_LA_BULLE = 'Couleur';
export const TRAIT_DE_LA_BULLE = 'Trait';
/** Sur la bulle du projet : sa couleur seulement, la forme et le trait se règlent dans l'Habit du projet. */
export const COULEUR_DU_PROJET_SEULEMENT = 'La forme et le trait du projet se règlent dans son Habit, en haut.';

/** Les quatorze objets, les noms de la maquette `formes-de-bulle.html`. */
export const MOTS_DES_OBJETS_DE_BULLE: Record<ObjetDeBulle, string> = {
  ampoule: 'Ampoule',
  telephone: 'Téléphone',
  ordinateur: 'Ordinateur',
  dossier: 'Dossier',
  fichier: 'Fichier',
  enveloppe: 'Enveloppe',
  calendrier: 'Calendrier',
  panier: 'Panier',
  outils: 'Boîte à outils',
  maison: 'Maison',
  billet: 'Billet',
  horloge: 'Horloge',
  nuage: 'Nuage',
  drapeau: 'Drapeau',
};

/** Les vingt-deux formes d'une bulle : les huit du projet, puis les objets. */
export const MOTS_DES_FORMES_DE_BULLE: Record<FormeDeBulle, string> = {
  ...MOTS_DES_FORMES_DE_PROJET,
  ...MOTS_DES_OBJETS_DE_BULLE,
};

/*
 * Les formes rangées par famille dans la fiche (retour d'Igor du 2026-10-21 : « les forme doivent
 * etre categorisé genre geometrique , document , idée , image »). Le menu « ⋯ » ne montre que les
 * quinze de décor : le galet et les quatorze objets.
 */
export const FAMILLE_GEOMETRIQUE = 'Géométrique';
export const FAMILLE_DOCUMENTS = 'Documents';
export const FAMILLE_IDEES = 'Idées';
export const FAMILLE_OBJETS = 'Objets';
